// Core utilities - DOM, Object, String, Array, Date, Validation, Performance
import { dom } from './dom.js';

export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(deepClone);
  
  const clone = {};
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      clone[key] = deepClone(obj[key]);
    }
  }
  return clone;
}

export function deepMerge(target, ...sources) {
  if (!sources.length) return target;
  
  const source = sources.shift();
  if (!source) return target;
  
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) target[key] = {};
        deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  
  return deepMerge(target, ...sources);
}

export function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export function pick(obj, keys) {
  return keys.reduce((result, key) => {
    if (key in obj) result[key] = obj[key];
    return result;
  }, {});
}

export function omit(obj, keys) {
  const result = { ...obj };
  keys.forEach(key => delete result[key]);
  return result;
}

export function capitalize(str) {
  if (!str || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function camelCase(str) {
  return str
    .replace(/[-_\s]+(.)?/g, (_, c) => c ? c.toUpperCase() : '')
    .replace(/^(.)/, c => c.toLowerCase());
}

export function kebabCase(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

export function truncate(str, maxLength, suffix = '...') {
  if (!str || str.length <= maxLength) return str;
  return str.substring(0, maxLength - suffix.length) + suffix;
}

export function groupBy(array, key) {
  return array.reduce((result, item) => {
    const groupKey = typeof key === 'function' ? key(item) : item[key];
    (result[groupKey] ||= []).push(item);
    return result;
  }, {});
}

// Remove duplicates from array
export function unique(array, comparator) {
  if (comparator) {
    return array.filter((item, index, self) => 
      self.findIndex(other => comparator(item, other)) === index
    );
  }
  return [...new Set(array)];
}

export function chunk(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export function formatDate(date, format = 'YYYY-MM-DD') {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const pad = n => String(n).padStart(2, '0');
  const parts = {
    YYYY: d.getFullYear(),
    MM: pad(d.getMonth() + 1),
    DD: pad(d.getDate()),
    HH: pad(d.getHours()),
    mm: pad(d.getMinutes()),
    ss: pad(d.getSeconds())
  };
  
  return format.replace(/YYYY|MM|DD|HH|mm|ss/g, match => parts[match]);
}

export function daysDiff(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return NaN;
  
  return Math.ceil(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24));
}

export function isValidEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.toLowerCase());
}

const KNOWN_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'icloud.com',
  'protonmail.com',
];

const COMMON_DOMAIN_TYPOS = {
  'gamil.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cmo': 'gmail.com',
  'yaho.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'outlok.com': 'outlook.com',
  'outllok.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'hotnail.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'iclod.com': 'icloud.com',
  'icloud.con': 'icloud.com',
  'protonmai.com': 'protonmail.com',
  'protonmail.con': 'protonmail.com',
};

function hasSingleSwapTypo(value, candidate) {
  if (!value || !candidate || value.length !== candidate.length) return false;
  const mismatchIndexes = [];
  for (let i = 0; i < value.length; i += 1) {
    if (value[i] !== candidate[i]) mismatchIndexes.push(i);
    if (mismatchIndexes.length > 2) return false;
  }
  if (mismatchIndexes.length !== 2) return false;
  const [first, second] = mismatchIndexes;
  return (
    second === first + 1 &&
    value[first] === candidate[second] &&
    value[second] === candidate[first]
  );
}

function hasSingleReplaceTypo(value, candidate) {
  if (!value || !candidate || value.length !== candidate.length) return false;
  let mismatches = 0;
  for (let i = 0; i < value.length; i += 1) {
    if (value[i] !== candidate[i]) mismatches += 1;
    if (mismatches > 1) return false;
  }
  return mismatches === 1;
}

function hasSingleInsertDeleteTypo(shorter, longer) {
  if (!shorter || !longer || longer.length - shorter.length !== 1) return false;
  let shorterIndex = 0;
  let longerIndex = 0;
  let edits = 0;

  while (shorterIndex < shorter.length && longerIndex < longer.length) {
    if (shorter[shorterIndex] === longer[longerIndex]) {
      shorterIndex += 1;
      longerIndex += 1;
      continue;
    }

    edits += 1;
    if (edits > 1) return false;
    longerIndex += 1;
  }

  return true;
}

function findSingleEditSuggestion(domain) {
  for (const knownDomain of KNOWN_EMAIL_DOMAINS) {
    if (domain === knownDomain) continue;
    if (hasSingleSwapTypo(domain, knownDomain)) return knownDomain;
    if (hasSingleReplaceTypo(domain, knownDomain)) return knownDomain;

    if (domain.length + 1 === knownDomain.length && hasSingleInsertDeleteTypo(domain, knownDomain)) {
      return knownDomain;
    }

    if (knownDomain.length + 1 === domain.length && hasSingleInsertDeleteTypo(knownDomain, domain)) {
      return knownDomain;
    }
  }
  return null;
}

export function getLikelyEmailDomainTypo(email) {
  if (typeof email !== 'string') return null;
  const normalizedEmail = email.trim().toLowerCase();
  if (!isValidEmail(normalizedEmail)) return null;

  const atIndex = normalizedEmail.lastIndexOf('@');
  if (atIndex <= 0 || atIndex >= normalizedEmail.length - 1) return null;

  const localPart = normalizedEmail.slice(0, atIndex);
  const typedDomain = normalizedEmail.slice(atIndex + 1);
  if (!typedDomain) return null;

  let suggestedDomain = COMMON_DOMAIN_TYPOS[typedDomain] || null;
  let reason = suggestedDomain ? 'known_typo' : 'none';

  if (!suggestedDomain && typedDomain.endsWith('.con')) {
    const candidateDomain = `${typedDomain.slice(0, -4)}.com`;
    if (KNOWN_EMAIL_DOMAINS.includes(candidateDomain)) {
      suggestedDomain = candidateDomain;
      reason = 'common_tld_typo';
    }
  }

  if (!suggestedDomain && typedDomain.endsWith('.cmo')) {
    const candidateDomain = `${typedDomain.slice(0, -4)}.com`;
    if (KNOWN_EMAIL_DOMAINS.includes(candidateDomain)) {
      suggestedDomain = candidateDomain;
      reason = 'common_tld_typo';
    }
  }

  if (!suggestedDomain) {
    suggestedDomain = findSingleEditSuggestion(typedDomain);
    if (suggestedDomain) reason = 'single_edit_typo';
  }

  if (!suggestedDomain || suggestedDomain === typedDomain) return null;

  return {
    typedEmail: normalizedEmail,
    typedDomain,
    suggestedDomain,
    suggestedEmail: `${localPart}@${suggestedDomain}`,
    reason,
  };
}

export function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function debounce(func, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = setTimeout(() => inThrottle = null, limit);
    }
  };
}

/**
 * Move the "default" item (id === '0') to the front of a list.
 * @param {any[]} list - Source array (not mutated).
 * @param {any[]} [fallbackList] - Used to inject a default when none exists in list.
 * @param {{ predicate?: (item: any) => boolean, sortComparator?: (a: any, b: any) => number, fallbackProps?: object }} [options]
 */
export function moveDefaultToFront(list = [], fallbackList = [], options = {}) {
  const {
    predicate = item => String(item?.id) === '0',
    sortComparator,
    fallbackProps = {},
  } = options;

  let normalized = Array.isArray(list) ? list.map(item => ({ ...item })) : [];
  if (typeof sortComparator === 'function') normalized.sort(sortComparator);

  const defaultIdx = normalized.findIndex(predicate);
  if (defaultIdx > 0) {
    const [entry] = normalized.splice(defaultIdx, 1);
    normalized.unshift(entry);
  } else if (defaultIdx === -1) {
    const fallbackDefault = (Array.isArray(fallbackList) ? fallbackList : []).find(predicate);
    if (fallbackDefault) normalized.unshift({ ...fallbackDefault, ...fallbackProps });
  }
  return normalized;
}

/**
 * Deduplicate an array of objects by their `id` field.
 * Items without an id or with a duplicate id are removed (first occurrence wins).
 * @param {any[]} items
 * @returns {any[]}
 */
export function dedupeById(items = []) {
  const seen = new Set();
  return items.filter(item => {
    const key = String(item?.id ?? '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Generate random alphanumeric ID
export function randomId(length = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

// Export all utilities
export default {
  dom,
  deepClone,
  deepMerge,
  isObject,
  pick,
  omit,
  capitalize,
  camelCase,
  kebabCase,
  truncate,
  groupBy,
  unique,
  chunk,
  formatDate,
  daysDiff,
  isValidEmail,
  getLikelyEmailDomainTypo,
  isValidUrl,
  debounce,
  throttle,
  randomId,
  moveDefaultToFront,
  dedupeById,
};

// Re-export dom for convenience
export { dom };
